import React from 'react';
import { styled } from '@mui/material/styles';
import TabulatedPage, {
    TabulatedPageTab,
} from 'ui/TabulatedPage/TabulatedPage';
import HeaderLayout from 'ui/HeaderLayout/HeaderLayout';
import {
    useHasAccessBudgetsGestion,
    useHasAccessCadrageGestion,
    useIsAdminCategories,
    useIsSupportUser,
} from 'hooks/user';
import ManageBudget from './BudgetManagement/ManageBudget';
import ManageProjects from './ProjectManagement/ManageProjects';
import { useGetBudgetProposalsWithResources } from './hooks';
import { useParams } from 'react-router-dom';
import { BudgetBys } from 'business/constants';
import NotFound from 'components/Errors/NotFound';
import ManageCadrage from './CadrageManagement/ManageCadrage';
import { useTranslation } from 'react-i18next';

const StyTabulatedPage = styled(TabulatedPage, {
    name: 'StyTabulatedPage',
})({
    height: '75%',
    '& .Mui-TabulatedPage-content-div': { height: '100%' },
});

enum BudgetSettingsTab {
    MANAGE_PROJECTS = 'manage_projects',
    MANAGE_BUDGET = 'manage_budget',
    MANAGE_CADRAGE = 'manage_cadrage',
}

export type BudgetSettingsUrlParams = {
    tab: string;
    by: BudgetBys | undefined;
};

export default function BudgetSettings(): React.ReactElement {
    const isSupportUser = useIsSupportUser();
    const hasBudgetsGestion = useHasAccessBudgetsGestion();
    const hasCadrageGestion = useHasAccessCadrageGestion();
    const isAdminCategories = useIsAdminCategories();
    const { t } = useTranslation();

    const tabsList: TabulatedPageTab[] = [];
    const { isFetching, data: budgetProposals } =
        useGetBudgetProposalsWithResources();

    const { tab, by: selectedBy } = useParams<BudgetSettingsUrlParams>();

    if (isSupportUser || isAdminCategories) {
        tabsList.push({
            label: t('adminBudgetSpace.manageProjects'),
            children: <ManageProjects />,
            pathname: `/budget_admin/${BudgetSettingsTab.MANAGE_PROJECTS}`,
            disabled: false,
        });
    }

    if (isSupportUser || hasCadrageGestion) {
        tabsList.push({
            label: t('adminBudgetSpace.manageCadrage'),
            children: (
                <ManageCadrage
                    budgetProposals={budgetProposals}
                    isLoading={isFetching}
                />
            ),
            pathname: `/budget_admin/${BudgetSettingsTab.MANAGE_CADRAGE}`,
            disabled: false,
        });
    }

    const maxExercice = budgetProposals
        ?.map((bp) => bp.exercice)
        .reduce((prev, current) => (prev > current ? prev : current), 0);
    const latestBpBy =
        budgetProposals?.find((bp) => bp.exercice === maxExercice)?.by ||
        BudgetBys.GESTIONNAIRE;

    if (isSupportUser || hasBudgetsGestion) {
        tabsList.push({
            label: t('adminBudgetSpace.manageBudget'),
            children: (
                <ManageBudget
                    budgetProposals={budgetProposals}
                    isLoading={isFetching}
                />
            ),
            pathname: `/budget_admin/${BudgetSettingsTab.MANAGE_BUDGET}/${
                selectedBy ? selectedBy : latestBpBy
            }`,
            disabled: false,
        });
    }

    if (!Object.values(BudgetSettingsTab).includes(tab as BudgetSettingsTab))
        return <NotFound />;

    return (
        <div className="page-container dx-viewport">
            <HeaderLayout title={t('adminBudgetSpace.budgetSettings')} />
            <StyTabulatedPage tabsList={tabsList} />
        </div>
    );
}
